/**
 * Centralized Socket.IO configuration utility
 * Handles URL construction, protocol detection, and transport configuration
 * 
 * Note: Socket.IO uses HTTP/HTTPS URLs, not ws:///wss:// URLs.
 * It handles the WebSocket upgrade internally based on the transport configuration.
 */

/**
 * Get the socket server URL
 * - Uses REACT_APP_API_URL from environment
 * - Falls back to localhost for development
 * - Socket.IO will handle protocol upgrades (http -> ws, https -> wss) automatically
 */
export const getSocketUrl = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3070';
};

/**
 * Get transport configuration based on environment
 * - Production: Prefer websocket, fallback to polling
 * - Development: Use websocket and polling
 * 
 * Note: Some hosting platforms (like Vercel) don't support WebSocket upgrades.
 * If you encounter issues, you can force polling by setting REACT_APP_SOCKET_TRANSPORT=polling
 */
export const getSocketTransports = () => {
  // Allow override via environment variable
  const transportOverride = process.env.REACT_APP_SOCKET_TRANSPORT;
  if (transportOverride) {
    return [transportOverride];
  }

  // Default: prefer websocket in production, allow both in development
  if (process.env.NODE_ENV === 'production') {
    // Try websocket first, fallback to polling if upgrade fails
    return ['websocket', 'polling'];
  }
  
  return ['websocket', 'polling'];
};

/**
 * Get socket.io client options with proper configuration
 * @param {string} token - Authentication token
 * @param {object} additionalOptions - Additional socket.io options
 */
export const getSocketOptions = (token, additionalOptions = {}) => {
  return {
    auth: { token },
    transports: getSocketTransports(),
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
    timeout: 20000,
    forceNew: false,
    ...additionalOptions,
  };
};
